import { logError } from '@databases/logs';
import Delta, { type Op } from 'quill-delta';
import { useEffect, useRef } from 'react';

export function getTextDelta(from: string, to: string, cursor?: number): Op[] {
  return new Delta().insert(from).diff(new Delta().insert(to), cursor).ops;
}

/**
 * Registers an input handler on a textarea that will not only be called with the new value
 * after an update, but also with a quill delta (which can be applied to a Y.Text element).
 */
export function useTextDelta(
  textarea: HTMLTextAreaElement | HTMLInputElement | undefined | null,
  onChange: (delta: Op[], newValue: string) => void
): void {
  const lastRange = useRef<[selectionStart: number, selectionEnd: number]>();
  const lastValue = useRef<string>();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!textarea) {
      return;
    }

    const handleSelectionChange = () => {
      if (textarea.selectionStart != null && textarea.selectionEnd != null) {
        lastRange.current = [textarea.selectionStart, textarea.selectionEnd];
      }
    };

    const handleInput = () => {
      const prevRange = lastRange.current;
      const prevValue = lastValue.current;
      handleSelectionChange();
      lastValue.current = textarea.value;
      const newValue = lastValue.current;

      if (!prevRange || prevValue == null) {
        logError(
          new Error(
            `Cannot create delta, some values are undefined: ${JSON.stringify({
              prevRange,
              prevValue,
            })}`
          )
        );
        return;
      }

      const delta = getTextDelta(prevValue, newValue, prevRange?.[1]);

      if (delta.length > 0) {
        onChangeRef.current(delta, newValue);
      }
    };

    lastValue.current = textarea.value;
    handleSelectionChange();

    textarea.ownerDocument.addEventListener('selectionchange', handleSelectionChange);
    textarea.addEventListener('input', handleInput);

    return () => {
      textarea.ownerDocument.removeEventListener(
        'selectionchange',
        handleSelectionChange
      );
      textarea.removeEventListener('input', handleInput);
    };
  }, [textarea]);

  useEffect(() => {
    // Update lastValue on every render in case the value was set by React
    lastValue.current = textarea?.value;
  });
}
